import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from '@rbx/intl';
import { Typography, makeStyles } from '@rbx/ui';
import { stickyBreadcrumbScrollOffset } from '@modules/ui/shared/utils/styles';
import {
  APIReferenceMember,
  APIReferenceProperty,
  APIReferenceEvent,
  APIReferenceMethod,
  APIReferenceCallback,
  isMathOperationMember,
  memberHasParameters,
  ReferenceType,
} from '@modules/client';
import { ApiTag } from '@modules/ui/tags/types';
import {
  labelTranslKeyByMemberType,
  urlHashByMemberTypeDetailSection,
} from '@modules/apiReference/page/constants/apiReferenceConstants';
import {
  IteratorName,
  classMemberTypeToIteratorName,
  orderedMemberTypes,
  ClassMemberType,
} from '@modules/apiReference/page/utils/apiReferenceUtils';
import ReferenceDivider from '../../sharedUI/components/ReferenceDivider';
import ReferenceMemberDetailContent from '../../sharedUI/components/ReferenceMemberDetailContent';
import { getUniqueMemberTitleHashId } from '../../utils/apiReferencePageNavUtils';
import { normalizeSecurityTags } from '../../utils/apiMemberUtils';
import { getVisibleReferenceTags } from '../../sharedUI/utils/tagUtils';

const useMemberDetailsContainerStyles = makeStyles()(() => ({
  sectionTitle: {
    marginBottom: '36px',
    scrollMarginTop: stickyBreadcrumbScrollOffset,
  },
  memberList: {
    marginBottom: '48px',
  },
}));

interface MemberDetailsContainerProps {
  properties: APIReferenceProperty[] | null;
  events: APIReferenceEvent[] | null;
  methods: APIReferenceMethod[] | null;
  callbacks: APIReferenceCallback[] | null;
}

function MemberDetailsContainer({
  ...referenceMembersByIteratorName
}: MemberDetailsContainerProps) {
  const { translate } = useTranslation();
  const { classes } = useMemberDetailsContainerStyles();

  const hasMembers = useCallback(
    (iteratorName: IteratorName) =>
      Array.isArray(referenceMembersByIteratorName[iteratorName]) &&
      (referenceMembersByIteratorName[iteratorName] ?? []).length > 0,
    [referenceMembersByIteratorName],
  );
  const hasSection = useCallback(
    (iteratorName: IteratorName) => hasMembers(iteratorName),
    [hasMembers],
  );
  const existingSectionsOrderedMemberTypes = useMemo(
    () =>
      (orderedMemberTypes(ReferenceType.CLASS) as ClassMemberType[]).filter((memberType) =>
        hasSection(classMemberTypeToIteratorName[memberType]),
      ),
    [hasSection],
  );

  if (existingSectionsOrderedMemberTypes.length === 0) return null;

  return (
    <Fragment>
      <ReferenceDivider />
      <Typography variant='h2' component='div' style={{ marginBottom: '24px' }}>
        {translate('Label.APIReference') || 'API Reference'}
      </Typography>
      {existingSectionsOrderedMemberTypes.map((memberType) => {
        const headerTranslKey = labelTranslKeyByMemberType[memberType];
        const iteratorName = classMemberTypeToIteratorName[memberType];
        const sectionId = urlHashByMemberTypeDetailSection[memberType].slice(1);
        const members = referenceMembersByIteratorName[iteratorName];

        return (
          <Fragment key={memberType}>
            <Typography
              id={sectionId}
              variant='largeLabel2'
              component='div'
              className={classes.sectionTitle}>
              {translate(headerTranslKey) || headerTranslKey}
            </Typography>
            {hasMembers(iteratorName) && (
              <section className={classes.memberList}>
                {members!.map((member, i) => {
                  const prevMember = members![i - 1] as APIReferenceMember;
                  const isSameNameAsPrevMember =
                    isMathOperationMember(member as APIReferenceMember) ||
                    !prevMember ||
                    isMathOperationMember(prevMember)
                      ? false
                      : member.name === prevMember.name;

                  const {
                    name,
                    summary,
                    deprecationMessage,
                    security,
                    threadSafety,
                    description,
                    codeSamples,
                    tags,
                  } = member;

                  // For canonical types: properties don't have parameters, others do
                  const paramsList = memberHasParameters(member)
                    ? (member.parameters ?? [])
                    : undefined;
                  // Create unique key for React (name + parameter names)
                  const paramsKey = paramsList?.map((param) => param.name).join('-');
                  const memberKey = `${name}-${paramsKey}`;

                  const id = getUniqueMemberTitleHashId(member, isSameNameAsPrevMember);
                  const securities = normalizeSecurityTags(security);
                  const allTags = getVisibleReferenceTags({
                    tags: (tags || []) as ApiTag[],
                    security: securities,
                    threadSafety,
                  });

                  return (
                    <ReferenceMemberDetailContent
                      key={memberKey}
                      name={name}
                      summary={summary}
                      allTags={allTags}
                      deprecationMessage={deprecationMessage}
                      description={description}
                      member={member}
                      memberType={memberType}
                      codeSamples={codeSamples}
                      id={id}
                    />
                  );
                })}
              </section>
            )}
          </Fragment>
        );
      })}
    </Fragment>
  );
}

export default MemberDetailsContainer;
